// src/components/TrafficSourceWrapper.js
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { sendCodedLog } from '../services/logService';
import Common from '../common/Common';
import useAdvancedScrollDetector from '../hooks/useAdvancedScrollDetector';
import { useAuth } from '../context/AuthContext';

const TrafficSourceWrapper = ({ children }) => {
  const location = useLocation();
  const { user } = useAuth();
  const { pathname } = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1); // Elimina el '#'
      setTimeout(() => {
        const element = document.getElementById(id);
        if (element) {
          element?.scrollIntoView({ behavior: 'smooth' });
        }
      }, 369); // Un pequeño retraso para asegurar que la página se haya renderizado
    } else {
      window.scrollTo(0, 0);
    }
  }, [location, pathname]); // Se ejecuta cada vez que cambia la URL

  const handleScrollStart = (startPosition) => {
    // Lógica para cuando el scroll empieza, si es necesaria.
  };

  const handleScrollStop = ({ direction, distance, startPosition, endPosition }) => {
    // sendCodedLog({
    //   message: `STOPPED SCROLLING | Direction: ${direction || 'N/A'} | Distance: ${distance}px | Start: ${startPosition} | End: ${endPosition}`,
    //   event: `scroll_stopped_${direction}`,
    //   ...Common.getCommonLogData(location)
    // });
  };

  const { isScrolling, scrollData } = useAdvancedScrollDetector({
    onScrollStart: handleScrollStart,
    onScrollStop: handleScrollStop,
    stopDelay: 333,
    threshold: 10,
  });

  useEffect(() => {
    let sessionId = sessionStorage.getItem('track_session_id');
    
    if (!sessionId) {
      sessionId = Common.generateSessionCode();
      sessionStorage.setItem('track_session_id', sessionId);
      
      sendCodedLog({
        message: `ID de sesión generado y guardado: ${sessionId}`,
        event: 'session_id_generated',
        ...Common.getCommonLogData(location)
      });
    }

    const queryParams = new URLSearchParams(location.search);
    const fromSource = queryParams.get('from') 
      || queryParams.get('fr');

    const email = queryParams.get('email') 
      || user?.email
      || localStorage.getItem('track_user_email') 
      || sessionStorage.getItem('track_user_email');

    const name = queryParams.get('name') 
      || user?.name
      || localStorage.getItem('track_user_name') 
      || sessionStorage.getItem('track_user_name');

    const existingSource = sessionStorage.getItem('track_traffic_source');
    const sourceToStore = fromSource || sessionStorage.getItem('track_traffic_source') || (user && 'user') || 'tk';

    email && localStorage.setItem('track_user_email', email);
    name && localStorage.setItem('track_user_name', name);

    if (fromSource) {
      if (existingSource) {
        if (existingSource !== fromSource) {
          sessionStorage.setItem('track_traffic_source', sourceToStore);

          sendCodedLog({
            message: `Fuente de tráfico diferente a la guardada: ${sourceToStore}`,
            event: 'traffic_source_saved',
            data: { source: sourceToStore, fromUrlParam: fromSource !== null },
            ...Common.getCommonLogData(location)
          });
        }
      } else {
        sessionStorage.setItem('track_traffic_source', sourceToStore);
        
        sendCodedLog({
          message: `Fuente de tráfico detectada y guardada: ${sourceToStore}`,
          event: 'traffic_source_saved',
          data: { source: sourceToStore, fromUrlParam: fromSource !== null },
          ...Common.getCommonLogData(location)
        });
      }
    } else {
      sessionStorage.setItem('track_traffic_source', sourceToStore);
    }
  }, [location.search, location]);

  useEffect(() => {
    const handleGlobalClick = (event) => {
      let currentElement = event.target;
      let clickedElementForLogging = null; // El elemento cuyas propiedades registraremos
      let clickIdentifier = null;         // La cadena que usaremos como ID de clic y para el nombre del evento

      // Recorre el árbol DOM desde el elemento clickeado hacia arriba
      while (currentElement && currentElement !== document.body) {
        // 1. Prioridad: atributo 'id'
        if (currentElement.id) {
          clickIdentifier = currentElement.id;
          clickedElementForLogging = currentElement;
          break; // Se encontró el mejor identificador, detén la búsqueda
        }

        // 2. Siguiente prioridad: atributo 'data-track-click'
        if (currentElement.hasAttribute('data-track-click')) {
          clickIdentifier = currentElement.dataset.trackClick;
          clickedElementForLogging = currentElement;
          break; // Se encontró el mejor identificador, detén la búsqueda
        }

        // 3. Si es un enlace (<a>), usa su 'href'
        if (currentElement.tagName === 'A' && currentElement.href) {
          clickIdentifier = currentElement.href;
          clickedElementForLogging = currentElement;
          break; // Se encontró el mejor identificador, detén la búsqueda
        }

        // 4. Si es un botón (<button>) o un enlace (<a>) con un atributo 'onclick', usa su contenido
        if ((currentElement.tagName === 'BUTTON' || currentElement.tagName === 'A') && currentElement.getAttribute('onclick')) {
          const onclickContent = currentElement.getAttribute('onclick');
          // Sanear y truncar el contenido de onclick para el identificador
          clickIdentifier = `onclick_${onclickContent.substring(0, 50).replace(/[^a-zA-Z0-9_]/g, '')}`;
          clickedElementForLogging = currentElement;
          break; // Se encontró el mejor identificador, detén la búsqueda
        }

        // 5. Última prioridad: Si es una imagen (<img>) y tiene 'src', usa su 'src'
        if (currentElement.tagName === 'IMG' && currentElement.src) {
          const url = new URL(currentElement.src, window.location.origin);
          // Tomamos el nombre del archivo o el hostname si no hay archivo en el path
          clickIdentifier = `img_src_${url.pathname.split('/').pop() || url.hostname}`;
          clickedElementForLogging = currentElement;
          break; // Se encontró el identificador, detén la búsqueda
        }

        currentElement = currentElement.parentElement;
      }

      // Si no se encontró un identificador específico después de recorrer el DOM,
      // usa el elemento original clickeado y proporciona un identificador genérico.
      if (!clickedElementForLogging) {
        clickedElementForLogging = null;
      }

      // Ahora registra el clic usando el elemento y el identificador determinados
      if (clickedElementForLogging) {
        const compellingData = {
          clickId: clickIdentifier, // Usa el ID determinado
          tagName: clickedElementForLogging.tagName,
          textContent: clickedElementForLogging.textContent.trim(),
          ...clickedElementForLogging.dataset, // Captura todos los atributos data-*
        };

        // Añade 'href' para enlaces a los datos adicionales
        if (clickedElementForLogging.tagName === 'A' && clickedElementForLogging.href) {
          compellingData.href = clickedElementForLogging.href;
        }

        // Añade el contenido de 'onclick' para botones/enlaces si estaba presente
        if ((clickedElementForLogging.tagName === 'BUTTON' || clickedElementForLogging.tagName === 'A') && clickedElementForLogging.getAttribute('onclick')) {
            compellingData.onclick = clickedElementForLogging.getAttribute('onclick');
        }

        // Añade 'src' y 'alt' para imágenes si estaba presente
        if (clickedElementForLogging.tagName === 'IMG') {
          if (clickedElementForLogging.src) compellingData.src = clickedElementForLogging.src;
          if (clickedElementForLogging.alt) compellingData.alt = clickedElementForLogging.alt;
        }


        // Sanea el clickIdentifier para el nombre del evento (si contiene caracteres especiales como /, :, etc.)
        let eventNameSuffix = clickIdentifier ? `_${clickIdentifier.replace(/[^a-zA-Z0-9_]/g, '_')}` : '';
        // Trunca el sufijo del nombre del evento si se vuelve demasiado largo
        if (eventNameSuffix.length > 100) {
            eventNameSuffix = eventNameSuffix.substring(0, 100);
        }

        sendCodedLog({
          message: `CLICK DETECTED | ${clickIdentifier} | ${compellingData.tagName} | "${compellingData.textContent}"`,
          event: `click${eventNameSuffix}`,
          data: compellingData,
          ...Common.getCommonLogData(location),
        });
      }
    };

    document.addEventListener('click', handleGlobalClick);

    return () => {
      document.removeEventListener('click', handleGlobalClick);
    };
  }, [location]);

  useEffect(() => {
    sendCodedLog({
      message: `MOUNTED`,
      event: `mounted`,
      ...Common.getCommonLogData(location),
    });
    
    return () => {
      sendCodedLog({
        message: `UNMOUNTED`,
        event: `unmounted`,
        ...Common.getCommonLogData(location),
      });
    };
  }, []);

  return <>{children}</>;
};

export default TrafficSourceWrapper;